import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALINSTITUTIONFINANCE_LOAD_BY_TRIAL_INSTITUTION_ID,
    ACTION_TRIALINSTITUTIONFINANCE_LOAD_BY_ID,
    ACTION_TRIALINSTITUTIONFINANCE_LOAD_FAILURE,
    ACTION_TRIALINSTITUTIONFINANCE_LOAD_SUCCESS,

    ACTION_TRIALINSTITUTIONFINANCE_SAVE,
    ACTION_TRIALINSTITUTIONFINANCE_SAVE_SUCCESS,
    ACTION_TRIALINSTITUTIONFINANCE_SAVE_FAILURE,

    ACTION_TRIALINSTITUTIONFINANCE_VALIDATE,
    ACTION_TRIALINSTITUTIONFINANCE_VALIDATE_SUCCESS,
    ACTION_TRIALINSTITUTIONFINANCE_VALIDATE_FAILURE,

    ACTION_TRIALINSTITUTIONFINANCE_CREATE,
    ACTION_TRIALINSTITUTIONFINANCE_CREATE_SUCCESS,
    ACTION_TRIALINSTITUTIONFINANCE_CREATE_FAILURE,

    ACTION_TRIALINSTITUTIONFINANCE_SYNC,
    ACTION_TRIALINSTITUTIONFINANCE_SYNC_SUCCESS,
    ACTION_TRIALINSTITUTIONFINANCE_SYNC_FAILURE,

    ACTION_TRIALINSTITUTIONFINANCE_CLEAR,
    ACTION_TRIALINSTITUTIONFINANCE_FILTER,

    TrialInstitutionFinanceAction
} from '../actions/trialInstitutionFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialInstitutionFinanceState {
    formData: Dtos.TrialInstitutionFinance | Dtos.TrialInstitutionFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialInstitutionFinance[];
    formLength: number;
}

const initialState: ITrialInstitutionFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialInstitutionFinanceState, _isLoading: boolean): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialInstitutionFinanceState,
    formData: Dtos.TrialInstitutionFinance | Dtos.TrialInstitutionFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialInstitutionFinanceState, formFilter: IFormFilter): ITrialInstitutionFinanceState {

    const items: Dtos.TrialInstitutionFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.accountNumber.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialInstitutionFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialInstitutionFinanceState, propertiesToClear?: string[]): ITrialInstitutionFinanceState {

    if (!propertiesToClear) {
        const newState: ITrialInstitutionFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialInstitutionFinanceState, _isSaving: boolean): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialInstitutionFinanceState,
    formData: Dtos.TrialInstitutionFinance | Dtos.TrialInstitutionFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialInstitutionFinanceState, _isValidating: boolean): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialInstitutionFinanceState,
    formData: Dtos.TrialInstitutionFinance | Dtos.TrialInstitutionFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialInstitutionFinanceState, _isCreating: boolean): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialInstitutionFinanceState,
    formData: Dtos.TrialInstitutionFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): ITrialInstitutionFinanceState {
    const newState: ITrialInstitutionFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialInstitutionFinanceReducer: Reducer<ITrialInstitutionFinanceState> = (state: ITrialInstitutionFinanceState = initialState, action: TrialInstitutionFinanceAction) => {
    switch (action.type) {
        case ACTION_TRIALINSTITUTIONFINANCE_LOAD_BY_TRIAL_INSTITUTION_ID:
        case ACTION_TRIALINSTITUTIONFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALINSTITUTIONFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALINSTITUTIONFINANCE_SAVE:
            return saving(state, true);
        case ACTION_TRIALINSTITUTIONFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALINSTITUTIONFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_CREATE:
            return creating(state, true);
        case ACTION_TRIALINSTITUTIONFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALINSTITUTIONFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALINSTITUTIONFINANCE_SYNC:
            return loading(state, true);
        case ACTION_TRIALINSTITUTIONFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALINSTITUTIONFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialInstitutionFinanceReducer;